import { RenderType, TFormFieldsAndRules_New, TFromFieldInfo_New } from '@verifime/components';

import { api } from '@verifime/api-definition';
import {
  COUNTRY_STATES,
  OtherIdentityDocumentOptions,
  TOption,
  validationRules,
} from '@verifime/utils';
import * as yup from 'yup';
import { TSupportedOcrDocumentType } from './utils';

/**
 * TODO: Needs to clean code further
 */

// Driver License
export type TDriverLicenceFields =
  | 'licenceName'
  | 'licenceCountryOfIssue'
  | 'licenceState'
  | 'licenceNumber'
  | 'licenceCardNumber'
  // | 'licenceVersionNumber' // Temporary remove, will add it in another PR with country configs
  | 'licenceExpiry';

export const driverLicenceFormFields: { [field in TDriverLicenceFields]: TFromFieldInfo_New } = {
  licenceName: {
    label: 'Licence Holder name',
    fieldName: 'licenceName',
    renderType: RenderType.Text,
    rules: validationRules.REQUIRED_STRING_RULES,
  },
  licenceCountryOfIssue: {
    label: 'Country of issue',
    fieldName: 'licenceCountryOfIssue',
    rules: validationRules.REQUIRED_STRING_RULES,
    renderType: RenderType.ComboBox,
    optionProvider: (value: string) =>
      api
        .getV1referencecountriessearch({ queries: { term: value } })
        .then((data) => data.map((country) => ({ code: country.alpha3, label: country.name }))),
  },

  licenceState: {
    label: 'State',
    fieldName: 'licenceState',
    renderType: RenderType.Select,
    rules: yup.mixed().when(['licenceCountryOfIssue'], ([countryOfIssueCode]) => {
      return validationRules.countryStateSelectInputRule(countryOfIssueCode);
    }),
    items: [
      {
        label: 'Not Applicable',
        code: '',
      },
      ...COUNTRY_STATES.AUS,
    ],
  },
  licenceNumber: {
    label: 'Licence number',
    fieldName: 'licenceNumber',
    renderType: RenderType.Text,
    rules: validationRules.REQUIRED_DRIVER_LICENCE_NUMBER_VALIDATION_RULES,
  },
  licenceCardNumber: {
    label: 'Licence Card Number (AU)',
    fieldName: 'licenceCardNumber',
    renderType: RenderType.Text,
    rules: validationRules.OPTIONAL_DRIVER_LICENCE_CARD_NUMBER_VALIDATION_RULES,
  },
  // licenceVersionNumber: {
  //   label: 'Licence Version Number (NZ)',
  //   fieldName: 'licenceVersionNumber',
  //   renderType: RenderType.Text,
  //   rules: validationRules.OPTIONAL_DRIVER_LICENCE_VERSION_NUMBER_VALIDATION_RULES,
  // },
  licenceExpiry: {
    label: 'Licence expiry',
    fieldName: 'licenceExpiry',
    renderType: RenderType.Date,
    rules: validationRules.REQUIRED_EXPIRY_DATE_VALIDATION_RULES,
  },
};

// Medicare Card
export const MEDICARE_CARD_COLOURS = ['Green', 'Blue', 'Yellow'];
export type TMedicareCardFields =
  | 'medicareCardNumber'
  | 'medicareCardColour'
  | 'medicareCardExpiry'
  | 'medicareCardIndividualRefNumber'
  | 'medicareCardFullName';

export const medicareCardFormFields: { [field in TMedicareCardFields]: TFromFieldInfo_New } = {
  medicareCardNumber: {
    label: 'Medicare Card Number',
    fieldName: 'medicareCardNumber',
    renderType: RenderType.Text,
    rules: validationRules.requiredStringRule({ min: 10, max: 11 }),
  },
  medicareCardColour: {
    label: 'Medicare Card Colour',
    fieldName: 'medicareCardColour',
    rules: validationRules.REQUIRED_STRING_RULES,
    renderType: RenderType.Select,
    items: MEDICARE_CARD_COLOURS.map((colour) => ({ code: colour, label: colour } as TOption)),
  },
  medicareCardExpiry: {
    label: 'Medicare Card Expiry',
    fieldName: 'medicareCardExpiry',
    renderType: RenderType.Date,
    rules: validationRules.requiredDateRule(),
    minDate: new Date(),
  },
  medicareCardIndividualRefNumber: {
    label: 'Individual Reference Number',
    fieldName: 'medicareCardIndividualRefNumber',
    rules: validationRules.REQUIRED_STRING_RULES,
    renderType: RenderType.Text,
  },
  medicareCardFullName: {
    label: 'Full Name (as it appears on card)',
    fieldName: 'medicareCardFullName',
    rules: validationRules.REQUIRED_FULL_NAME_VALIDATION_RULES,
    renderType: RenderType.Text,
  },
};

export const medicareCardFieldsAndRules: TFormFieldsAndRules_New = {
  row1: [medicareCardFormFields.medicareCardNumber],
  row2: [medicareCardFormFields.medicareCardColour],
  row3: [medicareCardFormFields.medicareCardExpiry],
};

export const medicareCardOwnersFieldsAndRules: TFormFieldsAndRules_New = {
  row4: [medicareCardFormFields.medicareCardIndividualRefNumber],
  row5: [medicareCardFormFields.medicareCardFullName],
};

// Passport
export type TPassportFields =
  | 'passportNumber'
  | 'passportName'
  | 'passportGivenNames'
  | 'passportFamilyName'
  | 'passportCountryOfIssue'
  | 'passportExpiry';

type TNameField = Extract<
  TPassportFields | TMedicareCardFields | TDriverLicenceFields,
  | 'passportName'
  | 'passportGivenNames'
  | 'passportFamilyName'
  | 'licenceName'
  | 'medicareCardFullName'
>;

const structuredNameFieldMap: Record<TNameField, boolean> = {
  passportName: false,
  passportGivenNames: true,
  passportFamilyName: true,
  licenceName: false,
  medicareCardFullName: false,
} as const;

export const passportFormFields: { [field in TPassportFields]: TFromFieldInfo_New } = {
  passportNumber: {
    label: 'Passport Number',
    fieldName: 'passportNumber',
    renderType: RenderType.Text,
    rules: validationRules.REQUIRED_STRING_RULES,
  },
  passportName: {
    label: 'Name on Your Passport',
    fieldName: 'passportName',
    renderType: RenderType.Text,
    rules: validationRules.OPTIONAL_STRING_RULES,
  },
  passportGivenNames: {
    label: 'Given Names on Your Passport',
    fieldName: 'passportGivenNames',
    renderType: RenderType.Text,
    rules: validationRules.OPTIONAL_STRING_RULES,
  },
  passportFamilyName: {
    label: 'Family Name on Your Passport',
    fieldName: 'passportFamilyName',
    renderType: RenderType.Text,
    rules: validationRules.OPTIONAL_STRING_RULES,
  },
  passportCountryOfIssue: {
    label: 'Country of Issue',
    fieldName: 'passportCountryOfIssue',
    rules: validationRules.REQUIRED_STRING_RULES,
    renderType: RenderType.ComboBox,
    optionProvider: (value: string) =>
      api
        .getV1referencecountriessearch({ queries: { term: value } })
        .then((data) => data.map((country) => ({ code: country.alpha3, label: country.name }))),
  },
  passportExpiry: {
    label: 'Expiry Date',
    fieldName: 'passportExpiry',
    renderType: RenderType.Date,
    rules: validationRules.REQUIRED_EXPIRY_DATE_VALIDATION_RULES,
  },
};

// Other Identity Document
export type TOtherIdentityDocumentFields =
  | 'otherDocumentType'
  | 'documentIdentifier'
  | 'countryOfIssue'
  | 'dateOfIssue'
  | 'dateOfExpiry';

const getOtherIdentityDocumentOptionsFromEnv = (): TOption[] => {
  const givenTypes = process.env.NEXT_PUBLIC_OTHER_DOCUMENT_TYPES?.split(',');

  if (!givenTypes) {
    return [];
  }

  return givenTypes
    .reduce((tally, type) => [...tally, OtherIdentityDocumentOptions[type]], [])
    .filter(Boolean);
};

export const otherIdentityDocumentFormFields: {
  [field in TOtherIdentityDocumentFields]: TFromFieldInfo_New;
} = {
  otherDocumentType: {
    label: 'Document Type',
    fieldName: 'otherDocumentType',
    renderType: RenderType.Select,
    rules: validationRules.REQUIRED_STRING_RULES,
    items: getOtherIdentityDocumentOptionsFromEnv(),
  },
  documentIdentifier: {
    label: 'Unique ID or number',
    fieldName: 'documentIdentifier',
    rules: validationRules.REQUIRED_STRING_RULES,
    renderType: RenderType.Text,
  },
  countryOfIssue: {
    label: 'Country of issue',
    fieldName: 'countryOfIssue',
    rules: validationRules.REQUIRED_STRING_RULES,
    renderType: RenderType.ComboBox,
    optionProvider: (value: string) =>
      api
        .getV1referencecountriessearch({ queries: { term: value } })
        .then((data) => data.map((country) => ({ code: country.alpha3, label: country.name }))),
  },
  dateOfIssue: {
    label: 'Date of issue',
    fieldName: 'dateOfIssue',
    renderType: RenderType.Date,
    rules: validationRules.requiredDateRule(),
  },
  dateOfExpiry: {
    label: 'Date of expiry',
    fieldName: 'dateOfExpiry',
    renderType: RenderType.Date,
    rules: validationRules.REQUIRED_EXPIRY_DATE_VALIDATION_RULES,
  },
};

export type TOcrFormFields =
  | TDriverLicenceFields
  | TMedicareCardFields
  | TPassportFields
  | TOtherIdentityDocumentFields;

const formFieldConfigMap: Record<TSupportedOcrDocumentType, Record<string, TFromFieldInfo_New>> = {
  DriverLicence: driverLicenceFormFields,
  MedicareCard: medicareCardFormFields,
  Passport: passportFormFields,
  OtherIdentityDocument: otherIdentityDocumentFormFields,
};

// Helper function for field inclusion logic
const shouldIncludeField = (fieldName: string, structuredName: boolean): boolean => {
  const nameFieldStructured = structuredNameFieldMap[fieldName as TNameField];

  // Non-name fields are always included
  if (nameFieldStructured === undefined) {
    return true;
  }

  // Name fields are included based on structured name preference
  return structuredName === nameFieldStructured;
};

/**
 * Generates form fields and validation rules for OCR document editing.
 *
 * @param documentType - The type of document being processed (DriverLicence, MedicareCard, etc.)
 * @param structuredName - Whether to use structured name fields (given names + family name)
 *                        or single name field. Defaults to false (single name field).
 *
 * @returns Form fields configuration object with row-based layout
 *
 * @example
 * // Generate passport fields with structured names
 * const fields = generateFormFieldsAndRules('Passport', true);
 *
 * // Generate driver licence fields with single name field
 * const fields = generateFormFieldsAndRules('DriverLicence');
 */
export const generateFormFieldsAndRules = (
  documentType: TSupportedOcrDocumentType,
  useStructuredName: boolean = false,
): TFormFieldsAndRules_New => {
  const formFields = formFieldConfigMap[documentType];

  return Object.values(formFields)
    .filter((field) => shouldIncludeField(field.fieldName, useStructuredName))
    .reduce(
      (result, field, index) => ({
        ...result,
        [`row${index}`]: [field],
      }),
      {} as TFormFieldsAndRules_New,
    );
};
